import React, { FC, useContext, useMemo } from "react";
import ConfigurationContext from "../../../infrastructure/context/configuration/configuration.context";
import UserContext from "../../../infrastructure/context/user/user.context";
import CheckUtils from "../../../utilities/check-utils";
import TranslationManager from "../../../infrastructure/translation-manager";
import TranslationsContext from "../../../infrastructure/context/translations/translations-context";
import { TranslationKeys } from "../../../infrastructure/const";
import DiscountsSummary from "../../common/discounts-summary";

type Props = {
  cartTableRef: React.RefObject<HTMLTableElement>;
  vatExemptApplied: boolean;
};

const PriceSection: FC<Props> = ({ cartTableRef, vatExemptApplied }) => {
  const translationsContext = useContext(TranslationsContext);
  const translationManager = new TranslationManager(translationsContext);

  const configurationContext = useContext(ConfigurationContext);
  const { cart } = useContext(UserContext);

  const {
    subTotalInclVat,
    subTotalExclVat,
    shippingPriceExclVat,
    shippingPriceInclVat,
    vat,
    vatExempt,
    discounts,
    totalDiscount,
    displayPrice,
    priceBreakdown,
  } = cart;

  const promoDiscountApplied = useMemo(
    () =>
      !CheckUtils.isNullOrEmptyString(totalDiscount) &&
      priceBreakdown.totalDiscount > 0,
    [priceBreakdown.totalDiscount, totalDiscount]
  );

  if (configurationContext.isFeatureFlagEnabled("hidePricesAndPayment"))
    return null;

  const displayPriceIncludingVat = configurationContext.getFeatureFlagValue(
    "displayPriceIncludingVat"
  );

  return (
    <table id="shopping-cart-totals-table" ref={cartTableRef}>
      <tbody>
        <tr>
          <td className="a-right">
            {displayPriceIncludingVat
              ? translationManager.getTranslation(
                  TranslationKeys.UK.CART_SUBTOTAL_INCLUDING_VAT
                )
              : translationManager.getTranslation(
                  TranslationKeys.UK.CART_SUBTOTAL_EXCLUDING_VAT
                )}
          </td>
          <td className="a-right">
            {" "}
            <span className="price">
              {displayPriceIncludingVat ? subTotalInclVat : subTotalExclVat}
            </span>{" "}
          </td>
        </tr>
        {!CheckUtils.isNullOrEmptyString(shippingPriceExclVat) && (
          <tr>
            <td className="a-right">
              {translationManager.getTranslation(TranslationKeys.CART_SHIPPING)}
            </td>
            <td className="a-right">
              {" "}
              <span className="price">
                {displayPriceIncludingVat
                  ? shippingPriceInclVat
                  : shippingPriceExclVat}
              </span>
            </td>
          </tr>
        )}
        <tr>
          <td className="a-right">
            {" "}
            {translationManager.getTranslation(TranslationKeys.UK.VAT)}{" "}
          </td>
          <td className="a-right">
            <span className="price">{vat}</span>
          </td>
        </tr>
        {vatExemptApplied && (
          <tr>
            <td className="a-right">
              {translationManager.getTranslation(TranslationKeys.UK.VAT_EXEMPT)}
            </td>
            <td className="a-right">
              <span className="price">-{vatExempt}</span>
            </td>
          </tr>
        )}
        <DiscountsSummary discounts={discounts} />
        {promoDiscountApplied && (
          <tr className="total-discount">
            <td className="a-right">
              {translationManager.getTranslation(
                TranslationKeys.CART_TOTALDISCOUNT
              )}
            </td>
            <td className="a-right">
              <span className="price">{totalDiscount}</span>
            </td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr>
          <td className="a-right" colSpan={1}>
            <strong>{translationManager.getTranslation(TranslationKeys.TOTAL)}</strong>
          </td>
          <td className="a-right">
            <strong>
              <span className="price">{displayPrice}</span>
            </strong>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

export default PriceSection;
